import { MemCommonMentionKind, MemCommonRichTextInputInstance } from "@mem-labs/common-editor";
import { PropsWithChildren } from "react";
import { PopoverProps } from "react-aria-components";
import { JsonObject } from "type-fest";
import { MDSDropdownItem } from "../../lists/dropdown-list-item/dropdown-list-item.types";

export enum MDSComboboxMode {
  Default = "default",
  Passive = "passive",
}

// Slot names for customization
export enum MDSComboboxSlotName {
  LeftAddon = "left",
  RightAddon = "right",
  ActionButton = "action",
}

export enum MDSComboboxVariant {
  Default = "default",
  Brand = "brand",
  Emphasis = "emphasis",
  Chrome = "chrome",
}

// State interface for the combobox reducer
export interface ComboboxState<T> {
  isOpen: boolean;
  query: string;
  isEmpty: boolean;
  isLoading: boolean;
  data: T[];
  hasSearched: boolean;
  highlightedItemId: string | null;
  mentions: MDSComboboxMention[];
  popoverMentionMode: "implicit" | "explicit";
}

// Action types for the combobox reducer
export type ComboboxAction<T> =
  | { type: "QUERY_CHANGED"; payload: string }
  | { type: "SEARCH_STARTED" }
  | { type: "SEARCH_COMPLETED"; payload: T[] }
  | { type: "SEARCH_FAILED" }
  | { type: "LOADING_STARTED" }
  | { type: "LOADING_STOPPED" }
  | { type: "POPOVER_OPENED" }
  | { type: "POPOVER_CLOSED" }
  | { type: "EMPTY_STATE_CHANGED"; payload: boolean }
  | { type: "HIGHLIGHT_CHANGED"; payload: string | null }
  | { type: "HIGHLIGHT_NEXT" }
  | { type: "HIGHLIGHT_PREVIOUS" }
  | { type: "RESET" }
  | { type: "POPOVER_MENTION_MODE_CHANGED"; payload: "implicit" | "explicit" };

export type MDSComboboxMention = {
  kind: MemCommonMentionKind;
  isExternal: boolean;
} & (
  | {
      kind: MemCommonMentionKind.Collection;
      collection_id: string;
      collection_title: string;
    }
  | {
      kind: MemCommonMentionKind.Note;
      note_id: string;
      note_primary_label: string;
    }
);

export type MDSComboboxFetchDataHandler<T extends MDSDropdownItem> = ({
  query,
  mentionSymbol,
}: {
  query: string;
  mentionSymbol?: string;
}) => Promise<MDSComboboxDataSource<T>>;

export type MDSComboboxSubmitHandler = (payload: {
  tiptapJsonContent: JsonObject;
  mdxContent: string;
  htmlContent: string;
  mentions: MDSComboboxMention[];
  implicitMentions?: MDSComboboxMention[];
}) => Promise<boolean>;

export interface MDSComboboxProps<T extends MDSDropdownItem> extends PropsWithChildren {
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  chatIsClosed?: boolean;
  popoverProps?: PopoverProps;
  autoFocus?: boolean;
  fetchData: MDSComboboxFetchDataHandler<T>;
  onSubmit?: MDSComboboxSubmitHandler;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: (height?: number) => void;
  inputRef?: React.RefObject<MemCommonRichTextInputInstance>;
  mode?: MDSComboboxMode;
  editorDimensions?: {
    minHeight?: number;
    maxHeight?: number;
    padding?: string;
  };
  setContent?: string;
  onContentModified?: () => void;
  className?: string;
  variant?: MDSComboboxVariant;
  fetchIsInstant?: boolean;
  mentions: MDSComboboxMention[];
  onMentionAdd?: (mention: MDSComboboxMention) => void;
  onMentionRemove?: (mentionId: string) => void;
  onMentionsClear?: () => void;
  implicitMentions?: MDSComboboxMention[];
  forceClose?: boolean;
  onLeftAddonButtonClick?: () => void;
}

export type MDSComboboxDataSource<T extends MDSDropdownItem> = {
  data: T[];
  error?: string;
  isLoading?: boolean;
};
